import React from 'react';
import { Tooltip, IconButton, TableRow, TableCell } from '@mui/material';
import { WarningAmber, Visibility } from '@mui/icons-material';
import {
  CLIENT_FALLBACK,
  normalizeColor,
  readableText,
  jourLabel,
  formatTime,
  formatDuration,
  isIncomplete,
  impossibleHigh,
  frameSx,
  computeRuns,
  sums,
} from './reportingHelpers';
import ObservationCell from './ObservationCell';
import LavagesCell from './LavagesCell';
import TotalsLavages from './TotalsLavages';
import {helper} from "../../../utils/helper.js";

// Bloc d'une journée : lignes par tournée (groupées par agence) + ligne de total.
export default function DayBlock({ date, rows, onShowDetail }) {
  const ordered = [...rows].sort(
    (a, b) =>
      (a.agenceLibelle ?? '').localeCompare(b.agenceLibelle ?? '') ||
      (a.clientLibelle ?? '').localeCompare(b.clientLibelle ?? '') ||
      (a.tourneeLibelle ?? '').localeCompare(b.tourneeLibelle ?? '')
  );
  const dayLen = ordered.length;
  const t = sums(ordered);

  const groups = [];
  ordered.forEach((r) => {
    const key = r.agenceLibelle ?? '—';
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.rows.push(r);
    else groups.push({ key, color: normalizeColor(r.agenceColor) || CLIENT_FALLBACK, rows: [r] });
  });

  let jourPlaced = false;

  return (
    <>
      {groups.map((group) => {
        const frame = group.color;
        const clientRuns = computeRuns(group.rows, (r) => r.clientLibelle ?? null);
        const equipageRuns = computeRuns(group.rows, (r) => r.equipage ?? null);
        const lastIdx = group.rows.length - 1;

        return group.rows.map((row, gi) => {
          const isBottom = gi === lastIdx;
          const cr = clientRuns[gi];
          const er = equipageRuns[gi];
          const clientBottom = cr.show && gi + cr.span - 1 === lastIdx;
          const equipBottom = er.show && gi + er.span - 1 === lastIdx;

          const incomplete = isIncomplete(row);
          const baseSx = {
            backgroundColor: incomplete ? '#fddcb6' : '#fff',
            color: '#222',
            py: 0.6,
            fontSize: 13,
            borderBottom: isBottom ? undefined : '1px solid rgba(0,0,0,0.08)',
          };
          const mid = (extra = {}) => ({ ...baseSx, ...extra, ...frameSx(frame, false, isBottom, false, false) });

          const showJour = !jourPlaced;
          if (showJour) jourPlaced = true;

          return (
            <TableRow key={row.id}>
              {showJour && (
                <TableCell
                  rowSpan={dayLen}
                  sx={{ backgroundColor: '#fff', color: '#213067', fontWeight: 'bold', verticalAlign: 'middle', textAlign: 'center', fontSize: 13, whiteSpace: 'nowrap', borderRight: '2px solid #213067' }}
                >
                  {jourLabel(date)}
                </TableCell>
              )}

              {cr.show && (
                <TableCell
                  rowSpan={cr.span}
                  sx={{
                    backgroundColor: frame,
                    color: readableText(frame),
                    verticalAlign: 'middle',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: 13,
                    ...frameSx(frame, false, clientBottom, true, false),
                  }}
                >
                  {row.clientLibelle ?? '—'}
                </TableCell>
              )}

              <TableCell sx={mid()}>
                {incomplete && (
                  <Tooltip title={`Tournée incomplète : ${(row.nombreLaves ?? 0) + (row.nombreLavageImpossible ?? 0)}/${row.nbBacsPrevu} bacs traités`} arrow>
                    <WarningAmber sx={{ fontSize: 15, color: '#ef6c00', verticalAlign: 'text-bottom', mr: 0.5 }} />
                  </Tooltip>
                )}
                {row.tourneeLibelle ?? '—'}
              </TableCell>
              <TableCell sx={mid({ textAlign: 'center' })}>
                <LavagesCell
                  laves={row.nombreLaves ?? 0}
                  imp={row.nombreLavageImpossible ?? 0}
                  prevu={row.nbBacsPrevu}
                  high={impossibleHigh(row)}
                />
              </TableCell>
              <TableCell sx={mid({ textAlign: 'center' })}>{helper.getFormattedTime(row.datePremiereIntervention)}</TableCell>
              <TableCell sx={mid({ textAlign: 'center' })}>{helper.getFormattedTime(row.dateDerniereIntervention)}</TableCell>
              <TableCell sx={mid({ textAlign: 'right' })}>{formatDuration(row.tempsTotalMinutes)}</TableCell>
              <TableCell sx={mid()}>{row.laveuseDescription ?? '—'}</TableCell>

              {er.show && (
                <TableCell
                  rowSpan={er.span}
                  sx={{ ...baseSx, verticalAlign: 'middle', textAlign: 'center', fontWeight: 600, ...frameSx(frame, false, equipBottom, false, false) }}
                >
                  {row.equipage ?? '—'}
                </TableCell>
              )}

              <TableCell sx={{ ...baseSx, ...frameSx(frame, false, isBottom, false, true) }}>
                <ObservationCell row={row} />
              </TableCell>

              <TableCell sx={{ ...baseSx, textAlign: 'center' }}>
                {row.campagneId && (
                  <Tooltip title="Détail des interventions" arrow>
                    <IconButton size="small" onClick={() => onShowDetail(row)} sx={{ p: 0.3 }}>
                      <Visibility sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          );
        });
      })}

      {/* Total journée */}
      <TableRow>
        <TableCell colSpan={3} sx={{ backgroundColor: '#37474F', color: 'white', fontWeight: 'bold', fontSize: 12.5 }}>
          Total {jourLabel(date)}
        </TableCell>
        <TableCell sx={{ backgroundColor: '#37474F', fontWeight: 'bold', textAlign: 'center' }}>
          <TotalsLavages t={t} color="#CFD8DC" />
        </TableCell>
        <TableCell colSpan={3} sx={{ backgroundColor: '#37474F' }} />
        <TableCell colSpan={4} sx={{ backgroundColor: '#37474F' }} />
      </TableRow>
    </>
  );
}
